/**
 * What this workspace has spent on AI.
 *
 * The first CenoX AI section with anything real behind it, and it is this one
 * because CenoX pays a provider the moment a customer uses the product. A
 * workspace that cannot see its consumption cannot manage it, and a platform
 * that cannot show it is asking to be trusted about a bill.
 *
 * Everything here comes from `GET /ai/usage`. Nothing is recomputed in the
 * browser: the server meters, prices and caps, and a second opinion formed
 * here would eventually disagree with the one that charges.
 *
 * What is deliberately absent: what CenoX paid its provider, the markup and
 * the margin. They are recorded on every usage event and they are not the
 * customer's business — the same rule the chat response follows.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AiUsageGroup, AiUsageTokens, AiUsageView } from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { count, currencyPrefix, date, money } from '../../lib/format';
import { Card, CardHeader, EmptyState, ErrorState, Spinner, cn } from '../../components/ui';

/** A token count as people read it: 12,400 rather than 12400. */
const tokens = (n: number): string => count(n);

/**
 * A charge at the ledger's precision, without the padding: "12.927341" rather
 * than "12.92734100", and never fewer than two places.
 */
const exact = (amount: string, currency: string): string =>
  `${currencyPrefix(currency)}${amount.replace(/(\.\d{2}\d*?)0+$/, '$1')}`;

/** `null` is no ceiling of that kind, which is not a ceiling of zero. */
const ceiling = (value: string | null, currency: string): string =>
  value === null ? 'No limit' : money(value, currency);

export function UsageView(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();

  const query = useQuery({
    queryKey: ['ai-usage', business?.businessId],
    queryFn: () => api.get<AiUsageView>(path('/ai/usage')),
    enabled: Boolean(business),
  });

  if (query.isPending) return <Spinner label="Loading usage" />;
  if (query.isError) return <ErrorState message="Could not load AI usage." />;

  const u = query.data;
  const currency = u.currencyCode;
  const byok = u.billingMode === 'bring_your_own_key';
  const allowance = u.monthlyAllowance
    ? { title: 'This allowance month', renews: 'renews monthly', per: 'this month' }
    : { title: 'This billing period', renews: 'renews with your plan', per: 'this period' };

  return (
    <div className="mx-auto w-full max-w-3xl">
      <h1 className="text-xl font-semibold tracking-tight text-slate-900">Usage</h1>
      <p className="mt-1 text-sm text-slate-500">
        {byok
          ? 'This workspace uses its own provider key, so your provider bills you directly. CenoX records what was used.'
          : `Tokens, requests and cost on the ${u.planName} plan.`}
      </p>

      {/* ── The credit position ──────────────────────────────────────────── */}
      <Card className="mt-4">
        {/*
          "Allowance month" only when it is one. With the monthly allowance on,
          it renews on the subscription's anniversary whatever the invoice
          interval. With it off, the window is the whole invoice period — a
          year for an annual subscriber — and "this month" would tell them
          they get the credit twelve times.
        */}
        <CardHeader
          title={allowance.title}
          subtitle={`${date(u.cycleStart, 'UTC')} to ${date(u.cycleEnd, 'UTC')} · ${allowance.renews}`}
        />
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label={allowance.title}>
          <Figure label="Requests" value={count(u.requestsThisCycle)} />
          <Figure label="Tokens" value={tokens(u.tokensThisCycle.total)} />
          {byok ? (
            <Figure label="Charged by CenoX" value={money('0', currency)} hint="your provider bills you" />
          ) : (
            <>
              <Figure label="Charged" value={money(u.chargedThisCycle, currency)} />
              <Figure
                label="Credit left"
                value={money(u.includedRemaining, currency)}
                hint={`of ${money(u.includedCredit, currency)} ${allowance.per}`}
                tone={Number(u.includedRemaining) <= 0 ? 'spent' : undefined}
              />
            </>
          )}
        </dl>
        {!byok && Number(u.topUpBalance) > 0 && (
          <p className="mt-3 text-xs text-slate-500">
            Top-up balance: {money(u.topUpBalance, currency)}. Included credit is used first.
          </p>
        )}
      </Card>

      {/* ── Where the tokens went ───────────────────────────────────────── */}
      <Card className="mt-4">
        <CardHeader
          title="Tokens by kind"
          subtitle="Priced differently, so the split is what tells you what to change"
        />
        <TokenSplit t={u.tokensThisCycle} />
      </Card>

      <Groups title="By model" rows={u.byTier} currency={currency} empty="Nothing used yet." />
      <Groups title="By feature" rows={u.byFeature} currency={currency} empty="Nothing used yet." />

      {/* ── The ceilings ────────────────────────────────────────────────── */}
      {!byok && (
        <Card className="mt-4">
          <CardHeader title="Limits" subtitle="Shown so a limit is visible before it is reached" />
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label="Limits">
            <Figure label="Per request" value={ceiling(u.limits.maxChargePerRequest, currency)} />
            <Figure
              label="Spent today"
              value={money(u.limits.spentToday, currency)}
              hint={`limit ${ceiling(u.limits.dailySpendCap, currency)}`}
            />
            <Figure label="This cycle" value={ceiling(u.limits.monthlySpendCap, currency)} />
            <Figure
              label="Tokens today"
              value={tokens(u.limits.tokensToday)}
              hint={
                u.limits.dailyTokenCap === null
                  ? 'no token limit'
                  : `limit ${tokens(u.limits.dailyTokenCap)}`
              }
            />
          </dl>
        </Card>
      )}

      {/* ── Every request ───────────────────────────────────────────────── */}
      <Card className="mt-4">
        <CardHeader
          title="Recent requests"
          subtitle="Newest first. Rows are rounded to two decimal places; hover a charge for the exact amount."
        />
        {u.history.length === 0 ? (
          <EmptyState
            title="No AI usage yet"
            description="Requests appear here as soon as the assistant is used."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Recent requests">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Model</th>
                  <th className="py-2 pr-3">Feature</th>
                  <th className="py-2 pr-3 text-right">Tokens</th>
                  <th className="py-2 text-right">Charged</th>
                </tr>
              </thead>
              <tbody>
                {u.history.map((h, i) => (
                  <tr key={`${h.at}-${i}`} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3 whitespace-nowrap text-xs text-slate-600">
                      {date(h.at, business?.timezone ?? 'UTC')}
                    </td>
                    <td className="py-2 pr-3">{h.label}</td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{h.feature}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {tokens(h.tokens.total)}
                      <div className="text-xs text-slate-400">
                        {tokens(h.tokens.output)} out
                      </div>
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {h.status === 'not_charged' ? (
                        // An absorbed failure. Said as what it was: free.
                        <span className="text-xs text-slate-500">Not charged</span>
                      ) : (
                        <span title={`${exact(h.chargedExact, currency)} exactly`}>
                          {money(h.charged, currency)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * The four kinds of token, as a readable bar plus the figures.
 *
 * Cached and cache-write are shown beside fresh input rather than folded into
 * it, because that is the difference between "we are re-sending context" and
 * "the provider is giving it to us cheaply", and only one of those is worth
 * acting on.
 */
function TokenSplit({ t }: { t: AiUsageTokens }): ReactNode {
  const parts = [
    { label: 'Fresh input', value: t.input, className: 'bg-brand-500' },
    { label: 'Cached input', value: t.cached, className: 'bg-brand-300' },
    { label: 'Cache writes', value: t.cacheWrite, className: 'bg-amber-300' },
    { label: 'Output', value: t.output, className: 'bg-slate-700' },
  ].filter((p) => p.value > 0);
  const sum = parts.reduce((a, p) => a + p.value, 0);

  if (sum === 0) {
    return (
      <p className="text-sm text-slate-500" aria-label="Tokens by kind">
        No tokens used in this cycle yet.
      </p>
    );
  }

  return (
    <>
      <div className="flex h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden>
        {parts.map((p) => (
          <div key={p.label} className={p.className} style={{ width: `${(p.value / sum) * 100}%` }} />
        ))}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label="Tokens by kind">
        {parts.map((p) => (
          <Figure key={p.label} label={p.label} value={tokens(p.value)} />
        ))}
      </dl>
    </>
  );
}

function Groups({
  title,
  rows,
  currency,
  empty,
}: {
  title: string;
  rows: AiUsageGroup[];
  currency: string;
  empty: string;
}): ReactNode {
  return (
    <Card className="mt-4">
      <CardHeader title={title} />
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label={title}>
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                <th className="py-2 pr-3">{title.replace('By ', '')}</th>
                <th className="py-2 pr-3 text-right">Requests</th>
                <th className="py-2 pr-3 text-right">Tokens</th>
                <th className="py-2 text-right">Charged</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-3 font-medium text-slate-900">{r.label}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{count(r.requests)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{tokens(r.tokens.total)}</td>
                  <td className="py-2 text-right tabular-nums">{money(r.charged, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'spent';
}): ReactNode {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-base font-semibold tabular-nums sm:text-lg',
          tone === 'spent' ? 'text-amber-700' : 'text-slate-900',
        )}
      >
        {value}
      </dd>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}
